import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import * as graphql from '@treams/treams-network';
import {
	Button,
	FlexGap,
	ForwardIcon,
	Heading3,
	Paragraph,
	Skeleton,
	Spinner,
	ViewIcon,
	tokens,
} from '@treams/treams-ui';

import { Recipient } from '../../../packages/kakapo-ui-services/models/Recipient';
import FeedbackRecipient from '../FeedbackRecipient';
import { FeedbackAddRecipientsModal } from './FeedbackAddRecipientsModal';

export function FeedbackRecipients(props: { request: graphql.FeedbackRequest }) {
	const { t } = useTranslation();
	const location = useLocation();
	const [selectorVisible, setSelectorVisible] = useState(false);

	const [internal, { loading: internalLoading }] =
		graphql.useRequestAdditionalInternalFeedbackMutation();
	const [external, { loading: externalLoading }] =
		graphql.useRequestAdditionalExternalFeedbackMutation();
	const [restore, { loading: loadingRestore }] = graphql.useRestoreFeedbackResponseMutation();

	const loading = internalLoading || externalLoading || loadingRestore;
	const viewingEveryone = !new URLSearchParams(location.search).get('response_id');
	const allPath = props.request.user.isViewer
		? `/feedback/${props.request.id}`
		: `/users/${props.request.user.id}/feedback/${props.request.id}`;

	const sorted = _.sortBy(
		props.request.responses.edges,
		({ node: { recipient } }) => `${recipient.firstName} ${recipient.lastName}`,
	);

	const filtered = sorted.filter(
		({ node }) =>
			!(node.status === graphql.FeedbackResponseStatus.Cancelled && node.answers?.length === 0),
	);

	const recipients = filtered.map(
		({ node: { recipient } }): Recipient =>
			'id' in recipient
				? {
						userId: recipient.id,
						userData: recipient as graphql.User,
					}
				: {
						name: `${recipient.firstName} ${recipient.lastName}`,
						email: recipient.email,
					},
	);

	const addRecipients = async (recipients) => {
		setSelectorVisible(false);

		for (const recipient of recipients) {
			if ('userId' in recipient) {
				// Can potentially be a cancelled invitee
				const responseID = sorted.find(
					({ node }) => 'id' in node.recipient && node.recipient.id === recipient.userId,
				)?.node.id;

				if (!responseID) {
					await internal({
						variables: {
							input: { requestID: props.request.id, userID: recipient.userId },
						},
					});
				} else {
					// Restore invitee instead
					await restore({
						variables: {
							input: { responseID },
						},
					});
				}

				return;
			}

			// Can potentially be a cancelled invitee
			const responseID = sorted.find(
				({ node }) =>
					'email' in node.recipient &&
					node.recipient.email?.toLowerCase() === recipient.email.toLowerCase(),
			)?.node.id;

			if (!responseID) {
				const [firstName, ...lastName] = recipient.name.split(' ');

				await external({
					variables: {
						input: {
							requestID: props.request.id,
							email: recipient.email,
							firstName,
							lastName: lastName.join(' '),
						},
					},
				});
			} else {
				// Restore invitee instead
				await restore({
					variables: {
						input: { responseID },
					},
				});
			}
		}
	};

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%" alignItems="flex-start">
			<FlexGap flexDirection="column" gap={tokens.gap.inline}>
				<Heading3>{t('feedback.results.questions.respondents.title')}</Heading3>
				<Paragraph small>{t('feedback.results.questions.respondents.description')}</Paragraph>
			</FlexGap>
			<Button
				type="secondary"
				icon={<ViewIcon />}
				title={t('feedback.results.questions.respondents.all')}
				disabled={viewingEveryone}
				link={viewingEveryone ? undefined : allPath}
			/>
			<FlexGap flexDirection="column" gap={tokens.gap.inline} width="100%">
				{filtered.map(({ node }) => (
					<FeedbackRecipient
						key={node.id}
						request={props.request}
						response={node as graphql.FeedbackResponse}
					/>
				))}
			</FlexGap>
			{loading ? (
				<Spinner />
			) : (
				<Button
					type="secondary"
					icon={<ForwardIcon />}
					title={t('users.collaborators.selector.add.buttonTitle')}
					onClick={() => setSelectorVisible(true)}
				/>
			)}
			{selectorVisible && (
				<FeedbackAddRecipientsModal
					recipients={recipients}
					onAddRecipients={addRecipients}
					onCancel={() => setSelectorVisible(false)}
				/>
			)}
		</FlexGap>
	);
}

export function FeedbackRecipientsSkeleton() {
	const { t } = useTranslation();

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%" alignItems="flex-start">
			<FlexGap flexDirection="column" gap={tokens.gap.inline}>
				<Heading3>{t('feedback.results.questions.respondents.title')}</Heading3>
				<Paragraph small>{t('feedback.results.questions.respondents.description')}</Paragraph>
			</FlexGap>
			<Button.Skeleton small />
			<FlexGap flexDirection="column" gap={tokens.gap.inline} width="100%">
				<Skeleton.Repeat count={3}>
					<FeedbackRecipient.Skeleton />
				</Skeleton.Repeat>
			</FlexGap>
		</FlexGap>
	);
}
